import { useQuery } from "@apollo/client/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_UI_COLORS, DATA_VIZ_COLORS } from "../../../config/chartColors";
import { DifficultyDistributionDocument } from "../../../generated/graphql";

interface DifficultyDistributionChartProps {
  slideId: string;
}

export function DifficultyDistributionChart({ slideId }: DifficultyDistributionChartProps) {
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(DifficultyDistributionDocument, {
    variables: {
      slideId,
    },
  });

  if (isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Error loading chart: {error.message}</div>
      </div>
    );
  }

  if (!data?.difficultyDistribution || data.difficultyDistribution.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">No data available</div>
      </div>
    );
  }

  const chartData = data.difficultyDistribution.map((bucket) => ({
    range: bucket.range,
    count: bucket.count,
  }));

  // If only one card, show difficulty value instead of chart
  const totalCards = chartData.reduce((sum, bucket) => sum + bucket.count, 0);
  if (totalCards === 1) {
    const cardBucket = chartData.find((bucket) => bucket.count > 0);
    const difficultyRange = cardBucket?.range || "Unknown";

    return (
      <div className="chart-container">
        <div className="flex flex-col items-center justify-center h-80">
          <div className="text-6xl font-bold text-secondary-200 mb-4">{difficultyRange}</div>
          <div className="h5">Difficulty Range (0-10)</div>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h4 className="h6">Difficulty Distribution</h4>
      </div>
      <div className="chart-body">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData}>
            <defs>
              <linearGradient id="difficultyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DATA_VIZ_COLORS.primary} stopOpacity={0.8} />
                <stop offset="100%" stopColor={DATA_VIZ_COLORS.secondary} stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="difficultyGradientHover" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DATA_VIZ_COLORS.primary} stopOpacity={1} />
                <stop offset="100%" stopColor={DATA_VIZ_COLORS.secondary} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_UI_COLORS.grid} />
            <XAxis
              dataKey="range"
              label={{
                value: "Difficulty Range",
                position: "insideBottom",
                offset: -5,
                fill: CHART_UI_COLORS.axis,
              }}
              tick={{ fill: CHART_UI_COLORS.axis }}
            />
            <YAxis
              allowDecimals={false}
              label={{
                value: "Number of Cards",
                angle: -90,
                position: "insideLeft",
                fill: CHART_UI_COLORS.axis,
              }}
              tick={{ fill: CHART_UI_COLORS.axis }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: CHART_UI_COLORS.tooltip.background,
                border: `1px solid ${CHART_UI_COLORS.tooltip.border}`,
                borderRadius: "0.5rem",
              }}
              labelStyle={{ color: CHART_UI_COLORS.tooltip.label }}
              itemStyle={{ color: CHART_UI_COLORS.tooltip.text }}
              cursor={{ fill: `${DATA_VIZ_COLORS.primary}26` }}
            />
            <Legend wrapperStyle={{ color: CHART_UI_COLORS.legend }} />
            <Bar
              dataKey="count"
              fill="url(#difficultyGradient)"
              activeBar={{
                fill: "url(#difficultyGradientHover)",
                stroke: DATA_VIZ_COLORS.primary,
                strokeWidth: 2,
              }}
              name="Card Count"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
